import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe } from "lucide-react";

const LOGO = "/logo.png";

const languages = [
  { code: 'az', label: 'AZ' },
  { code: 'ru', label: 'RU' },
  { code: 'en', label: 'EN' },
];

export default function Navbar({ language, onLanguageChange, t }) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const langRef = useRef(null);

  // Close language dropdown on outside click / Escape
  useEffect(() => {
    if (!langMenuOpen) return;
    const onDown = (e) => {
      if (langRef.current && !langRef.current.contains(e.target)) setLangMenuOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setLangMenuOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("touchstart", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("touchstart", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [langMenuOpen]);

  // Lock body scroll + Escape while mobile menu is open
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setMenuOpen(false);
    document.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      document.removeEventListener("keydown", onKey);
    };
  }, [menuOpen]);

  const handleNavClick = (href) => {
    setMenuOpen(false);
    setLangMenuOpen(false);
    // wait a tick so the body scroll lock is released before scrolling
    setTimeout(() => {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    }, 60);
  };

  const navLinks = [
    { label: t.nav.home, href: "#hero" },
    { label: t.nav.about, href: "#about" },
    { label: t.nav.services, href: "#services" },
    { label: t.nav.gallery, href: "#gallery" },
    { label: t.nav.reviews, href: "#testimonials" },
    { label: t.nav.contact, href: "#contact" },
  ];

  return (
    <>
      <nav
        data-testid="navbar"
        className={`fixed top-0 left-0 right-0 z-[70] transition-colors duration-500 ${
          scrolled || menuOpen
            ? "bg-black/95 backdrop-blur-sm border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between h-20">
          <a
            href="#hero"
            onClick={(e) => { e.preventDefault(); handleNavClick("#hero"); }}
            className="flex items-center gap-3 hover:opacity-80 transition-opacity duration-300"
            data-testid="navbar-logo"
          >
            <img
              src={LOGO}
              alt="Orkhan Babayev Logo"
              className="h-12 w-auto object-contain"
            />
          </a>

          <div className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <a
                key={link.href}
                href={link.href}
                onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                className="text-[11px] tracking-[0.22em] uppercase text-white/70 hover:text-[#D4AF37] transition-colors duration-300 font-body"
                data-testid={`nav-link-${link.label}`}
              >
                {link.label}
              </a>
            ))}
          </div>

          <div className="flex items-center gap-4">
            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1.5 text-xs tracking-wider uppercase text-white/70 hover:text-[#D4AF37] transition-colors duration-300 font-body px-3 min-h-[44px]"
                aria-label="Change language"
                aria-haspopup="true"
                aria-expanded={langMenuOpen}
              >
                <Globe size={14} />
                {language.toUpperCase()}
              </button>
              
              <AnimatePresence>
                {langMenuOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 bg-[#0F0F0F] border border-white/10 py-1 min-w-[80px]"
                  >
                    {languages.map((lang) => (
                      <button
                        key={lang.code}
                        onClick={() => {
                          onLanguageChange(lang.code);
                          setLangMenuOpen(false);
                        }}
                        className={`w-full text-left px-4 py-3 text-xs tracking-wider uppercase font-body transition-colors duration-200 ${
                          language === lang.code
                            ? "text-[#D4AF37] bg-white/5"
                            : "text-white/70 hover:text-white hover:bg-white/5"
                        }`}
                      >
                        {lang.label}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <a
              href="tel:+994555525001"
              className="hidden sm:inline-flex items-center px-7 py-3 border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black transition-colors duration-300 text-[11px] tracking-[0.22em] uppercase font-body font-semibold"
              data-testid="navbar-cta"
            >
              {t.nav.bookNow}
            </a>
            <button
              className="lg:hidden text-white hover:text-[#D4AF37] transition-colors duration-300 w-11 h-11 flex items-center justify-center -mr-2"
              onClick={() => { setMenuOpen(!menuOpen); setLangMenuOpen(false); }}
              aria-expanded={menuOpen}
              data-testid="navbar-menu-button"
              aria-label={t.nav.menu}
            >
              {menuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </nav>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="fixed inset-0 z-[60] bg-[#050505] flex flex-col items-center justify-center overflow-y-auto pt-20 pb-8"
          >
            <nav className="flex flex-col items-center gap-7 my-auto">
              {navLinks.map((link, i) => (
                <motion.a
                  key={link.href}
                  href={link.href}
                  onClick={(e) => { e.preventDefault(); handleNavClick(link.href); }}
                  initial={{ opacity: 0, y: 18 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.06, duration: 0.35 }}
                  className="text-2xl font-heading text-white hover:text-[#D4AF37] transition-colors duration-300 tracking-wider"
                  data-testid={`mobile-nav-${link.label}`}
                >
                  {link.label}
                </motion.a>
              ))}
              
              {/* Language options in mobile menu */}
              <motion.div
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: navLinks.length * 0.06, duration: 0.35 }}
                className="flex gap-4 mt-2"
              >
                {languages.map((lang) => (
                  <button
                    key={lang.code}
                    onClick={() => { onLanguageChange(lang.code); setMenuOpen(false); }}
                    className={`text-xs tracking-wider uppercase font-body px-4 min-h-[44px] border transition-colors duration-200 ${
                      language === lang.code
                        ? "text-[#D4AF37] border-[#D4AF37]"
                        : "text-white/60 border-white/25 hover:text-white"
                    }`}
                  >
                    {lang.label}
                  </button>
                ))}
              </motion.div>

              <motion.a
                href="tel:+994555525001"
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: (navLinks.length + 1) * 0.06, duration: 0.35 }}
                className="mt-3 px-10 py-4 border border-[#D4AF37] text-[#D4AF37] text-[11px] tracking-[0.28em] uppercase font-body font-semibold"
                data-testid="mobile-cta"
              >
                {t.nav.bookNow}
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
